<script setup>
import {onMounted, ref} from "vue";
import { auth, database } from '@/firebase'
import { ref as dbRef, push, onValue, remove } from "firebase/database";
import ArgonAlert from '@/components/ArgonAlert.vue';
import {isAuthenticated} from "../utils/auth";

const isLoading = ref(false);
const isAdding = ref(false);

const showDialog = ref(false);
const showConfirmDeleteDialog = ref(false);
const cardToDelete = ref(null);

const loadCards = ref([]);
const flippedState = ref({});
const newCard = ref({
  front: "",
  back: "",
  example: "",
});

const errors = ref({
  front: '',
  back: '',
  example: '',
});

const alertMessage = ref('');
const alertColor = ref('success');
const showAlert = ref(false);

const validateForm = () => {
  let valid = true;
  errors.value.front = '';
  errors.value.back = '';

  if (!newCard.value.front.trim()) {
    errors.value.front = 'Mặt trước không được để trống.';
    valid = false;
  }
  if (!newCard.value.back.trim()) {
    errors.value.back = 'Mặt sau không được để trống.';
    valid = false;
  }
  if (!newCard.value.example.trim()) {
    errors.value.example = 'Ví dụ không được để trống.';
    valid = false;
  }

  return valid;
};

const displayAlert = (message, color = 'success', duration = 3000) => {
  alertMessage.value = message;
  alertColor.value = color;
  showAlert.value = true;
  if (duration > 0) {
    setTimeout(() => {
      showAlert.value = false;
    }, duration);
  }
};

const closeDialog = () => {
  showDialog.value = false;
  newCard.value = { front: "", back: "", example: "" };
};

const openConfirmDeleteDialog = (cardId) => {
  cardToDelete.value = cardId;
  showConfirmDeleteDialog.value = true;
};

const closeConfirmDeleteDialog = () => {
  showConfirmDeleteDialog.value = false;
  cardToDelete.value = null;
};

const addCard = async () => {
  const uid = auth.currentUser?.uid;
  if (!isAuthenticated()) {
    displayAlert("Bạn cần đăng nhập để thêm thẻ ghi nhớ.", "danger", 5000);
    closeDialog();
    return;
  }
  if (!validateForm()) return;

  try {
    isAdding.value = true;
    const userFlashcardsRef = dbRef(database, `users/${uid}/flashcards`);
    await push(userFlashcardsRef, newCard.value);
    displayAlert("Thẻ ghi nhớ đã được thêm thành công!", "success");
    closeDialog();
  } catch (error) {
    console.error("Lỗi khi thêm thẻ ghi nhớ:", error);
    displayAlert("Đã xảy ra lỗi khi thêm thẻ ghi nhớ. Vui lòng thử lại.", "danger", 0);
  } finally {
    isAdding.value = false;
  }
};

const flipCard = (cardId) => {
  flippedState.value[cardId] = !flippedState.value[cardId];
};

onMounted(() => {
  const uid = auth.currentUser?.uid;
  if (!isAuthenticated()) {
    displayAlert("Bạn chưa đăng nhập. Thẻ ghi nhớ sẽ không được lưu hoặc tải.", "info", 0);
    return;
  }

  const flashcardsRef = dbRef(database, `users/${uid}/flashcards`);

  isLoading.value = true;

  onValue(
      flashcardsRef,
      (snapshot) => {
        const data = snapshot.val() || {};
        const cardsArray = Object.entries(data).map(([id, value]) => ({ id, ...value }));
        loadCards.value = cardsArray;

        const initialFlippedState = {};
        cardsArray.forEach(card => {
          initialFlippedState[card.id] = flippedState.value[card.id] || false;
        });
        flippedState.value = initialFlippedState;

        isLoading.value = false;
      },
      (error) => {
        console.error("Lỗi khi tải thẻ ghi nhớ:", error);
        displayAlert("Không thể tải thẻ ghi nhớ. Vui lòng kiểm tra kết nối hoặc quyền truy cập.", "danger", 0);
        isLoading.value = false;
      }
  );
});


const confirmDelete = async () => {
  const uid = auth.currentUser?.uid;
  if (!uid) {
    displayAlert("Bạn cần đăng nhập để xóa thẻ ghi nhớ.", "danger", 5000);
    closeConfirmDeleteDialog();
    return;
  }

  if (!cardToDelete.value) {
    displayAlert("Không có thẻ ghi nhớ nào được chọn để xóa.", "warning");
    closeConfirmDeleteDialog();
    return;
  }

  try {
    const cardRef = dbRef(database, `users/${uid}/flashcards/${cardToDelete.value}`);
    await remove(cardRef);
    displayAlert('Thẻ ghi nhớ đã được xóa thành công!', "success");
    delete flippedState.value[cardToDelete.value];
    closeConfirmDeleteDialog();
  } catch (error) {
    console.error("Lỗi khi xóa thẻ ghi nhớ:", error);
    displayAlert("Đã xảy ra lỗi khi xóa thẻ ghi nhớ. Vui lòng thử lại.", "danger", 0);
    closeConfirmDeleteDialog();
  }
};

const deleteFlashcard = (cardId) => {
  openConfirmDeleteDialog(cardId);
};
</script>
<template>
  <div class="py-4 container-fluid">
    <div class="text-end mb-3">
      <button class="btn btn-dark" @click="showDialog = true">Thêm thẻ ghi nhớ</button>
    </div>

    <div v-if="showAlert" class="mb-4">
      <ArgonAlert :color="alertColor" dismissible>
        {{ alertMessage }}
      </ArgonAlert>
    </div>

    <div v-if="showDialog" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0, 0, 0, 0.5);">
      <div class="modal-dialog">
        <div class="modal-content border-radius-md">
          <div class="modal-header">
            <h5 class="modal-title">Thêm Flash Card mới</h5>
            <button type="button" class="btn-close" @click="closeDialog"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Mặt trước (English)</label>
              <input v-model="newCard.front" type="text" class="form-control" :class="{'is-invalid': errors.front}"/>
              <div class="invalid-feedback">{{ errors.front }}</div>
            </div>
            <div class="mb-3">
              <label class="form-label">Mặt sau (Tiếng Việt)</label>
              <input v-model="newCard.back" type="text" class="form-control" :class="{'is-invalid': errors.back}"/>
              <div class="invalid-feedback">{{ errors.back }}</div>
            </div>
            <div class="mb-3">
              <label class="form-label">Ví dụ (Example)</label>
              <input v-model="newCard.example" type="text" class="form-control" :class="{'is-invalid': errors.example}"/>
              <div class="invalid-feedback">{{ errors.example }}</div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeDialog">Hủy</button>
            <button class="btn btn-dark" @click="addCard" :disabled="isAdding">
              <span v-if="isAdding" class="spinner-border spinner-border-sm me-2" role="status"
                    aria-hidden="true"></span>
              Thêm
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showConfirmDeleteDialog" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0, 0, 0, 0.5);">
      <div class="modal-dialog"> <div class="modal-content border-radius-md">
        <div class="modal-header">
          <h5 class="modal-title text-danger">Xác nhận xóa</h5>
          <button type="button" class="btn-close" @click="closeConfirmDeleteDialog"></button>
        </div>
        <div class="modal-body">
          Bạn có chắc chắn muốn xóa flashcard này không?
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeConfirmDeleteDialog">Không</button>
          <button class="btn btn-danger" @click="confirmDelete">Xóa</button>
        </div>
      </div>
      </div>
    </div>

    <div class="row">
      <div v-if="isLoading" class="text-center p-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      <div
          v-for="card in loadCards"
          :key="card.id"
          class="col-md-4 mb-4"
      >
        <div class="card shadow border-radius-md h-100">
          <div class="card-body d-flex flex-column justify-content-between">
            <div class="d-flex justify-content-between align-items-start">
              <h5 class="card-title mb-0">
                {{ flippedState[card.id] ? card.back : card.front }}
              </h5>
              <button
                  class="btn btn-link text-danger p-0 ms-auto"
                  @click="deleteFlashcard(card.id)" title="Xóa thẻ ghi nhớ"
              >
                <i class="fas fa-trash-alt fa-lg"></i>
              </button>
            </div>
            <p class="card-text text-muted" v-if="flippedState[card.id]">
              {{ card.example }}
            </p>
            <button
                class="btn btn-outline-dark mt-3"
                @click="flipCard(card.id)"
            >
              Lật thẻ
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="loadCards.length === 0" class="text-center text-white">
      Chưa có flashcard nào. Hãy thêm cái mới!
    </div>
  </div>
</template>

<style scoped>
.card-title {
  font-weight: bold;
  font-size: 1.25rem;
}
.card-text {
  font-size: 0.95rem;
}
.modal.show.d-block {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1050;
  overflow: auto;
}
</style>

<style scoped>
.card-title {
  font-weight: bold;
  font-size: 1.25rem;
}
.card-text {
  font-size: 0.95rem;
}
</style>
